// Types et libellés du module interventions (interface).

export type TypeElement = 'hypothese' | 'controle_propose' | 'controle_realise' | 'observation' | 'mesure' | 'cause_confirmee'
  | 'operation' | 'escalade' | 'note' | 'securite';
export type StatutElement = 'a_faire' | 'fait' | 'non_fait' | 'sans_objet' | 'ecartee' | 'confirmee' | null;

export interface SourceElement { version_id: string | null; page: number | null; chunk_id: string | null; titre: string; version_label: string | null; statut: string | null }

/** Intervention passée citée par une hypothèse reprise du « Déjà vu » (id null = plus consultable par ce lecteur). */
export interface SourceIntervention { id: string | null; reference: string | null; date: string | null }

export interface Element {
  id: string; kind: TypeElement; text: string; justification: string | null; value: string | null; unit: string | null;
  status: StatutElement; origin: 'manuel' | 'regle' | 'ia'; position: number; created_at: string; auteur: string | null;
  source: SourceElement | null;
  source_intervention?: SourceIntervention | null;
}

// ─── « Déjà vu » : pannes résolues comparables ───
export type NiveauDejaVu = 'meme_equipement' | 'meme_reference' | 'meme_code_fabricant' | 'meme_code';

export interface PanneResolue {
  id: string; reference: string; titre: string;
  niveau: NiveauDejaVu; niveau_libelle: string; pertinence_faible: boolean;
  statut: 'resolue' | 'cloturee'; date: string; date_libelle: string; auteur: string | null;
  site: { id: string; nom: string };
  equipement: { id: string; libelle: string; fabricant: string | null; reference: string | null } | null;
  symptome: string | null; codes_communs: string[];
  causes: { id: string; texte: string; detail: string | null; auteur: string | null; deja_reprise?: boolean }[];
  operations: string[]; mesures: { point: string; valeur: string; unite: string }[]; resolution: string | null;
  lien_rapport: string;
}

export interface ResultatDejaVu {
  criteres: { equipement?: boolean; fabricant: string | null; reference: string | null; codes?: string[] };
  resultats: PanneResolue[];
}

export interface ContexteIntervention {
  depuis?: string | null; frequence?: 'permanente' | 'intermittente' | null; conditions?: string | null; alarmes?: string | null;
  codes?: string[]; modification_recente?: 'oui' | 'non' | 'inconnu' | null; modifications?: string | null; remarques?: string | null;
}

export interface Intervention {
  id: string; reference: string; title: string; status: string; symptom: string | null; context: ContexteIntervention;
  resolution: string | null; escalation: string | null; created_at: string; updated_at: string; closed_at: string | null;
  site_id: string; equipment_id: string | null; modifiable: boolean;
  site: { id: string; name: string; demo: boolean; client: { id: string; name: string } | null };
  equipement: null | {
    id: string; label: string; category: string | null; manufacturer: string | null; range_name: string | null; reference: string | null;
    hw_version: string | null; sw_version: string | null; firmware: string | null; serial_number: string | null; protocols: string[];
    network_address: string | null; systeme: string | null; chemin: { id: string; kind: string; name: string }[];
  };
  intervenant: { id: string; nom: string } | null;
  elements: Element[];
}

export interface InterventionResume {
  id: string; reference: string; title: string; status: string; symptom: string | null; created_at: string; updated_at: string;
  site_id: string; site_name: string; demo: boolean; equipment_id: string | null; equipement: string | null; equipement_reference: string | null;
  equipement_fabricant: string | null; intervenant: string | null; mienne: boolean; controles_a_faire: number;
}

export interface Piste {
  chunk_id: string; version_id: string; document_id: string; titre: string; doc_type: string; version_label: string; statut: string;
  page: number; localisation: string | null; extrait: string; exact: boolean; versions_couvertes: string | null;
  version: 'confirmee' | 'non_confirmee' | 'inconnue'; deja_ajoutee: boolean; code_cite: boolean; ocr?: boolean;
}

export interface ResultatPistes {
  criteres: { references: string[]; codes: string[]; termes: string[]; fabricant: string | null; gamme: string | null };
  validees: Piste[]; nonValidees: Piste[];
  versionEquipement: { logicielle: string | null; firmware: string | null; materielle: string | null } | null;
}

export const UNITES = ['V', 'mV', 'A', 'mA', 'Ω', 'kΩ', '°C', '%HR', '%', 'Pa', 'bar', 'm³/h', 'ppm', 'kWh', 'W', 'Hz', 's', 'autre'] as const;

export const LIBELLES_ELEMENT: Record<TypeElement, string> = {
  hypothese: 'Hypothèse', controle_propose: 'Contrôle proposé', controle_realise: 'Contrôle réalisé', observation: 'Observation',
  mesure: 'Mesure', cause_confirmee: 'Cause confirmée', operation: 'Opération', escalade: 'Escalade', note: 'Note', securite: 'Sécurité',
};

export const LIBELLES_STATUT_ELEMENT: Record<string, string> = {
  a_faire: 'À faire', fait: 'Fait', non_fait: 'Non fait', sans_objet: 'Sans objet', ecartee: 'Écartée', confirmee: 'Confirmée',
};

export const DEPUIS = ['Aujourd’hui', 'Quelques jours', 'Plusieurs semaines', 'Depuis la mise en service', 'Inconnu'];

export const TYPES_LIEU: Record<string, string> = { batiment: 'Bâtiment', etage: 'Étage', zone: 'Zone' };

/** Valeur exacte venue du serveur (« 29.60 ») affichée à la française (« 29,60 »), sans arrondi. */
export const valeurFr = (v: string | null) => (v === null ? '' : v.replace('.', ','));
